import { MM_PER_LEGACY_UNIT } from "../sim/fixed";
import type { ScreenWavingEvent } from "./creaturemesh";

/** A camera-target offset in millimetres. */
export interface WaveOffset {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

interface Pending {
  readonly entityId: number;
  readonly dueMs: number;
  readonly durationS: number;
  readonly power: number;
  readonly range: number;
}

const STILL: WaveOffset = { xMm: 0, yMm: 0, zMm: 0 };

/** Frame-bucketed stand-in for `rand()`, so a replay draws the same jitter. */
function draw(seed: number, modulus: number): number {
  const value = Math.imul(seed ^ (seed >>> 16), 0x45d9f3b);
  return ((value ^ (value >>> 13)) >>> 0) % modulus;
}

/** The screen waves motions ask for: `ProcessEventScreenWaving` fires each
 *  `MotionEventType 2` block at its motion time when the body playing it is
 *  within the block's range of the main character, `SetScreenEffectWaving`
 *  keeps the latest, and `SetPositionCamera` jitters the camera target while it
 *  lasts (`ActorInstanceMotionEvent.cpp:52-54`, `GameEventManager.cpp:15-27`,
 *  `GrpBase.cpp:261-268,477-481`). */
export class ScreenWaves {
  private pending: Pending[] = [];
  private startMs = 0;
  private endMs = 0;
  private power = 0;

  /** Queue a clip's waves, started at `bornMs` and played `speedRatio` times fast. */
  schedule(entityId: number, bornMs: number, events: readonly ScreenWavingEvent[], speedRatio: number): void {
    const ratio = speedRatio > 0 ? speedRatio : 1;
    for (const event of events) {
      this.pending.push({
        entityId, dueMs: bornMs + (event.t * 1000) / ratio,
        durationS: event.durationS, power: event.power, range: event.range,
      });
    }
  }

  /** Fire every wave that is due, measured from the main character's position. */
  step(
    nowMs: number,
    positionOf: (entityId: number) => WaveOffset | null,
    centre: WaveOffset,
  ): void {
    if (this.pending.length === 0) return;
    const waiting: Pending[] = [];
    for (const wave of this.pending) {
      if (wave.dueMs > nowMs) {
        waiting.push(wave);
        continue;
      }
      const at = positionOf(wave.entityId);
      if (at === null) continue;
      const dx = at.xMm - centre.xMm;
      const dy = at.yMm - centre.yMm;
      const dz = at.zMm - centre.zMm;
      const reachMm = wave.range * MM_PER_LEGACY_UNIT;
      if (dx * dx + dy * dy + dz * dz > reachMm * reachMm) continue; // GameEventManager.cpp:24-25
      this.startMs = wave.dueMs;
      this.endMs = wave.dueMs + wave.durationS * 1000; // GrpBase.cpp:479
      this.power = wave.power;
    }
    this.pending = waiting;
  }

  /** The camera target's offset at `nowMs`: `rand() % power / 10` legacy units
   *  on each axis, the y axis flipped as `SetCenterPosition` flips it
   *  (`GrpBase.cpp:263-267`, `GameEventManager.cpp:4-9`). */
  offsetAt(nowMs: number): WaveOffset {
    if (this.power <= 0 || nowMs < this.startMs || nowMs >= this.endMs) return STILL;
    const seed = Math.imul(Math.floor(nowMs / 16), 0x9e3779b9) ^ Math.imul(this.startMs | 0, 0x1f123bb5);
    const axis = (salt: number): number => (draw(seed ^ salt, this.power) / 10) * MM_PER_LEGACY_UNIT;
    const y = axis(0x6d2b79f5);
    return { xMm: axis(0x27d4eb2d), yMm: y === 0 ? 0 : -y, zMm: axis(0x165667b1) };
  }
}
